import { Component, ElementRef, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BroadcastService, ListOption, WafFormHelper } from '../../../../modules/tcm-core.module';
import { NoteStatusEnum } from '../../../../models/enums';
import { UserOptionWithUserInfo } from '../../../../models/users';
import { WorkQueueNoteLinkedSearchRequest, WorkQueueNoteLinkedSearchResponse } from '../../../../models/work-queue-notes';
import { BaseSecureSearch } from '../../../../modules/tcm-common';
import { SecureBaseComponentContext } from '../../../../modules/tcm-common/services/secure-base-component-context.service';
import { AuthService, ListOptionService, PatientDataService, UserClinicService, UserDataService } from '../../../../services';
import { TranslationService } from '../../../../services/translation.service';
import { BroadCastEvents } from '../../../../common/broad-cast-events.enum';
import { ClinicListOption } from '../../../../models/clinics/clinic-list-option';
import { DateTimeHelper } from '../../../../helpers/date-time-helper';

@Component({
    selector: 'work-queue-note-linked-search',
    templateUrl: './work-queue-note-linked-search.component.html',
    styleUrls: ['./work-queue-note-linked-search.component.scss'],
    standalone: false
})
export class WorkQueueNoteLinkedSearchComponent extends BaseSecureSearch<WorkQueueNoteLinkedSearchRequest, WorkQueueNoteLinkedSearchResponse> {

  noteTypeTranslations: any;
  noteStatusTranslations: any;
  noteStatusOptions: ListOption<number>[];
  NoteStatusEnum = NoteStatusEnum;
  clinicId: number;
  assignedUserId: number;
  activeClinicOptions: ClinicListOption[];
  public workQueueId: number;

  @ViewChild(NgForm) private nestedNgForm: NgForm;
  @ViewChild(NgForm, { read: ElementRef }) private nestedNgFormElementRef: ElementRef;

  getPatientOptionsFunc = (query: string, cancellationToken: Promise<any>): Promise<ListOption<number>[]> => {
    return this.patientDataService.getPatientOptionsAsync(query, cancellationToken);
  }

  getUserOptionsFunc = (query: string, cancellationToken: Promise<any>): Promise<UserOptionWithUserInfo[]> => {
    return this.userDataService.getUserOptionsWithUserInfoAsync(query, cancellationToken);
  }

  constructor(protected override componentContext: SecureBaseComponentContext,
    private router: Router,
    protected override route: ActivatedRoute,
    private patientDataService: PatientDataService,
    private userDataService: UserDataService,
    private userClinicService: UserClinicService,
    private listOptionService: ListOptionService,
    private translationService: TranslationService,
    private authService: AuthService) {
    super(componentContext, route);
    this.actionName = 'WorkQueueNotes/WorkQueueNotes';

    route.params.subscribe((params) => {
      this.workQueueId = params["workQueueId"];
    });

    this.noteTypeTranslations = this.translationService.getNoteTypeTranslations();
    this.noteStatusTranslations = this.translationService.getNoteStatusTranslations();
    this.noteStatusOptions = this.listOptionService.getNoteStatusOptions();
    this.clinicId = this.userClinicService.clinicId;
    this.assignedUserId = !this.specialActionPermissions.allowToSeeClinicWorkQueues.canExecute ? this.authService.currentUserValue.id : null;    
    this.componentContext.broadcastService.next({ name: BroadCastEvents.WorkQueueTouched });    
    this.addCommonSubscription(this.componentContext.broadcastService.getEvents(BroadCastEvents.NoteChanged).subscribe(() => { this.refresh(); }));        
  }

  goToButtonClick($event: any, url: string, queryParams: any) {
    $event.stopPropagation();
    let urlWithQuery = url + "?params=" + encodeURIComponent(JSON.stringify(queryParams));
    window.open(urlWithQuery, '_blank');
  }

  onClinicChanged() {
    this.userClinicService.clinicId = this.clinicId;
    this.requestModel.clinicId = this.clinicId;

    this.refresh();    
  }
  
  onAssignedUserChanged() {
    this.requestModel.assignedUserId = this.assignedUserId;

    this.viewModel.records.forEach(x => {
      this.assignedUserId = this.assignedUserId;
    });    

    this.onFilterSubmitted();    
  }

  override onFilterCleared(): void {
    super.onFilterCleared();

    this.requestModel.assignedUserId = !this.specialActionPermissions.allowToSeeClinicWorkQueues.canExecute ? this.authService.currentUserValue.id : null;
    this.requestModel.clinicId = this.specialActionPermissions.allowToSeeClinicWorkQueues.canExecute ? this.clinicId : null;
    this.requestModel.followUpFromDate = null;
    this.requestModel.followUpToDate = null;
    this.requestModel.patientId = null;
    this.requestModel.statuses = null;
    this.requestModel.workQueueId = this.workQueueId;
  }

  override async refresh() {
    if (this.nestedNgForm && !this.nestedNgForm.valid) {
      WafFormHelper.invalidateForm(this.nestedNgForm, this.nestedNgFormElementRef.nativeElement, true);
      return;
    }

    this.requestModel.clinicId = this.specialActionPermissions.allowToSeeClinicWorkQueues.canExecute ? this.clinicId : null;
    this.requestModel.assignedUserId = this.assignedUserId;
    this.requestModel.workQueueId = this.workQueueId;

    super.refresh();
  }

  protected override createRequestModel() {
    const request = super.createRequestModel();
    request.clinicId = this.specialActionPermissions.allowToSeeClinicWorkQueues.canExecute ? this.clinicId : null;
    request.assignedUserId = this.assignedUserId;
    request.statuses = [NoteStatusEnum.New];
    request.workQueueId = this.workQueueId;

    return request as any;
  }

  protected override onSearchTermChanged() {
    this.requestModel = {
      clinicId: this.specialActionPermissions.allowToSeeClinicWorkQueues.canExecute ? this.clinicId : null,
      assignedUserId: this.assignedUserId,
    } as any;

    this.refresh();
  }

  protected override onViewModelChanged() {
    super.onViewModelChanged();

    if (this.requestModel.assignedUserId) {
      this.assignedUserId = this.requestModel.assignedUserId
    }

    this.activeClinicOptions = this.viewModel.clinicOptions.filter(x => x.isActive);
    const workQueueNotesLoadedArgs = { workQueueId: this.requestModel.workQueueId, total: this.viewModel.total };
    this.componentContext.broadcastService.next({ name: BroadCastEvents.WorkQueueNotesLoaded, args: workQueueNotesLoadedArgs });
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    
    this.router.routeReuseStrategy.shouldReuseRoute = function (future, curr) {
      return future.routeConfig === curr.routeConfig;
    };    
  }  

}
